/**
 * Shared type definitions for the Radar platform dashboard and tools.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface ToolState {
  status:
    | 'idle'
    | 'queued'
    | 'running'
    | 'done'
    | 'error'
    | 'skipped'
    | 'locked'
  data: any
  error: string | null
  startedAt: number | null
  finishedAt: number | null
}

export interface ThreadLog {
  time: number
  tool: ToolKey | 'system'
  message: string
  type: 'info' | 'success' | 'error' | 'insight'
}

export interface Insight {
  type: 'conflict' | 'good' | 'bad'
  title: string
  message: string
  // NO findingType here, deliberately. generateInsights produces CROSS-TOOL
  // conclusions ("crawl says X but citation says Y") whose provenance is not
  // determinable from a single `source` field, and nothing populated the
  // field when it was briefly added. An optional field nothing sets is worse
  // than no field: it implies a contract that does not exist. Classifying
  // insights needs the per-finding provenance work that T5 explicitly defers.
}

export interface SavedRun {
  id: string
  domain: string
  timestamp: number
  unifiedScore: number
  unifiedGrade: string
  toolScores: Record<string, { score: number; grade: string }>
  toolData: Record<string, any>
  /** Client label for agency workflows */
  clientLabel?: string
  /** Internal notes for agency workflows */
  notes?: string
}

/**
 * Win Log entry. `citation_flip` means a linked source on the audited domain
 * appeared; `mention_flip` means the brand started being named. A mention is
 * never recorded as a citation.
 */
export interface WinLogEntry {
  id: string
  domain: string
  provider: string
  date: number
  type:
    | 'citation_flip'
    | 'mention_flip'
    | 'score_improvement'
    | 'hallucination_fixed'
  description: string
  /** Which fixes were resolved before the flip */
  resolvedFixes?: string[]
  /** Previous run ID for comparison */
  previousRunId?: string
}

export interface RubricRef {
  tool: ToolKey
  category: string
  /**
   * Cap on the points this action may claim in the category, for a fix that
   * only PARTIALLY closes the gap as a side effect. Omit when the action
   * genuinely addresses the whole remaining gap. Must be derived from real
   * data (a count of items the fix adds), never hand-tuned to a nice number.
   */
  maxClaim?: number
}

import type { FindingType } from '@/lib/radar/finding-type'

/**
 * Where an action's execution priority came from.
 *
 *   measured    - real inputs; priorityScore is monthly clicks per
 *                 developer hour
 *   heuristic   - reserved: defensible static inputs, none exist today
 *   monitoring  - watch/investigate work with no implementable fix
 *   unranked    - an implementable fix with no traffic evidence
 */
export type ActionPrioritySource =
  | 'measured'
  | 'heuristic'
  | 'monitoring'
  | 'unranked'

export interface ActionPriorityInputs {
  /** Clicks per month. Null when no traffic evidence exists. */
  trafficAtStake: number | null
  /** 0..1 share of that traffic the fix plausibly recovers. */
  recoveryRate: number | null
  /** 0..1 certainty in the estimate. */
  confidence: number | null
  /** Developer hours. Must be > 0 to be usable. */
  effortHours: number | null
}

export interface ActionPriority extends ActionPriorityInputs {
  /**
   * (traffic * recovery * confidence) / hours — monthly clicks recovered
   * per DEVELOPER HOUR. Null when the inputs cannot support the claim.
   */
  priorityScore: number | null
  prioritySource: ActionPrioritySource
}

/**
 * Sources that produce actions but are NOT scored Radar tools.
 *
 * The GSC join checks read stored Search Console rows rather than auditing
 * the site, so they have no TOOLS entry, no rubric category and no score
 * contribution. Kept as a separate union rather than widened into ToolKey:
 * ToolKey drives the audit runner, the scored-section grouping and
 * projection's rubric attribution, and adding a non-scoring member there
 * would leak a non-tool into all three.
 */
export type NonToolActionSource = 'gsc-cannibalization' | 'gsc-visibility'

export type ActionSource = ToolKey | NonToolActionSource

export interface ActionItem {
  id: string
  category:
    | 'crawlability'
    | 'structured-data'
    | 'llm-communication'
    | 'citations'
    | 'community'
    | 'aeo'
    | 'answer-engine'
    | 'source-influence'
    | 'sov'
    | 'hallucination'
  title: string
  why: string
  how: string
  effort: 'low' | 'medium' | 'high'
  /**
   * Hand-authored severity, NOT a measurement. It survives only as the
   * fallback tie-break for unranked actions (see action-priority.ts) and
   * must never be surfaced as an evidence-backed "High Impact" claim.
   */
  impact: 'low' | 'medium' | 'high'
  /**
   * 1-based rank in canonical order. Retained for compatibility — nothing
   * in the repo reads it today, but it is part of the exported shape, so
   * it is kept and made to AGREE with the canonical sort rather than
   * carrying a second, conflicting ordering.
   */
  priority: number
  /** Execution priority. Separate from rubric points by design. */
  priorityDetail?: ActionPriority
  /**
   * Whether the underlying conclusion is measured or interpretive.
   * ORTHOGONAL to prioritySource: that says how execution priority is
   * handled, this says how the finding was reached. A monitoring action can
   * be deterministic, and a ranked action could be judgement.
   *
   * Optional for historical compatibility — pre-T5 records carry no value
   * and are never rewritten or guessed.
   */
  findingType?: FindingType
  /**
   * Which check produced this. A ToolKey for scored tools, or a
   * NonToolActionSource for producers like the GSC join checks that emit
   * recommendations without contributing to any score.
   */
  source: ActionSource
  relatedUrl?: string
  methodology?: string
  /**
   * Pointer to the rubric category this action recovers points in. Deliberately
   * a POINTER, never a point value: the points stay owned by the tool that
   * computes them, so a rubric reweight can never silently desync from the
   * roadmap. Absent for monitoring tools, whose scores are not recovered by
   * shipping a fix (see PriorityRoadmap: monitoring tools are held constant).
   */
  rubricRef?: RubricRef | RubricRef[]
  /**
   * Relative share of its category's remaining points this action claims when
   * several actions target the same category. Defaults to 1 (equal split).
   * Set it to the count of underlying items — 4 blocked bots, 3 incomplete
   * schema types — so an action addressing more of the gap claims more of it.
   */
  rubricWeight?: number
  /**
   * This action creates an artifact another tool scores but cannot yet see, so
   * that tool's generator is silent until it exists. Shipping it recovers the
   * named tool's MINIMAL_VALID_BASELINE and unlocks the rest of its rubric.
   *
   * The action is counted ONCE: it contributes its own `rubricRef` points to
   * its source tool and the baseline to the foundational tool, never the
   * combined total to both.
   */
  rubricFoundational?: { tool: ToolKey }
}

/**
 * Access tier:
 *   1 = free (anyone can run)
 *   2 = paid (requires session/token, enforced server-side via requirePaidTier)
 */
// `shortName` is the compact label for dense surfaces — the history row's
// per-tool chips, where "Brand Disambiguation" would blow the row width.
// Kept here so the long and short forms cannot drift apart.
export const TOOLS = [
  {
    key: 'crawl' as const,
    name: 'Crawl Check',
    shortName: 'Crawl',
    color: '#008dcc',
    endpoint: '/api/platform/tools/crawl-check',
    needsBrand: false,
    estSeconds: 15,
    tier: 1 as const,
  },
  {
    key: 'robots' as const,
    name: 'Robots.txt',
    shortName: 'Robots',
    color: '#8b5cf6',
    endpoint: '/api/platform/tools/robots-analyzer',
    needsBrand: false,
    estSeconds: 10,
    tier: 1 as const,
  },
  {
    key: 'llms' as const,
    name: 'llms.txt',
    shortName: 'llms.txt',
    color: '#22c55e',
    endpoint: '/api/platform/tools/llms-validator',
    needsBrand: false,
    estSeconds: 10,
    tier: 1 as const,
  },
  {
    key: 'readiness' as const,
    name: 'AI Readiness',
    shortName: 'AI Readiness',
    color: '#FDC304',
    endpoint: '/api/platform/tools/ai-readiness',
    needsBrand: false,
    estSeconds: 20,
    tier: 1 as const,
  },
  {
    key: 'citation' as const,
    name: 'Citations',
    shortName: 'Citations',
    color: '#F90B8A',
    endpoint: '/api/platform/tools/citation-check',
    needsBrand: true,
    estSeconds: 30,
    tier: 2 as const,
  },
  {
    key: 'reddit' as const,
    name: 'Reddit',
    shortName: 'Reddit',
    color: '#f97316',
    endpoint: '/api/platform/tools/reddit-monitor',
    needsBrand: true,
    estSeconds: 25,
    tier: 2 as const,
  },
  {
    key: 'aeo' as const,
    name: 'AEO Audit',
    shortName: 'AEO',
    color: '#7c3aed',
    endpoint: '/api/platform/tools/aeo-auditor',
    needsBrand: false,
    estSeconds: 15,
    tier: 1 as const,
  },
  {
    key: 'answer' as const,
    name: 'Citation Test',
    shortName: 'Citation Test',
    color: '#dc2626',
    endpoint: '/api/platform/tools/answer-engine',
    needsBrand: true,
    estSeconds: 45,
    tier: 2 as const,
  },
  {
    key: 'sourceInfluence' as const,
    name: 'Source Influence',
    shortName: 'Source',
    color: '#06b6d4',
    endpoint: '/api/platform/tools/source-influence',
    needsBrand: true,
    estSeconds: 40,
    tier: 2 as const,
  },
  {
    key: 'promptSov' as const,
    name: 'Prompt SOV',
    shortName: 'Prompt SOV',
    color: '#a855f7',
    endpoint: '/api/platform/tools/prompt-sov',
    needsBrand: true,
    estSeconds: 45,
    tier: 2 as const,
  },
  {
    key: 'schemaAudit' as const,
    name: 'Schema Audit',
    shortName: 'Schema',
    color: '#14b8a6',
    endpoint: '/api/platform/tools/schema-audit',
    needsBrand: false,
    estSeconds: 15,
    tier: 1 as const,
  },
  {
    key: 'hallucinationCheck' as const,
    name: 'Hallucination Check',
    shortName: 'Hallucination',
    color: '#ef4444',
    endpoint: '/api/platform/tools/hallucination-check',
    needsBrand: true,
    estSeconds: 40,
    tier: 2 as const,
  },
  {
    key: 'brandDisambiguation' as const,
    name: 'Brand Disambiguation',
    shortName: 'Brand',
    color: '#0ea5e9',
    endpoint: '/api/platform/tools/brand-disambiguation',
    needsBrand: true,
    estSeconds: 40,
    tier: 2 as const,
  },
] as const

export type ToolKey = (typeof TOOLS)[number]['key']

/* eslint-enable @typescript-eslint/no-explicit-any */
